<script setup>
// Assistente de IA — em quais caixas de entrada o assistente atende.
//
// A tela mostra TODAS as caixas da conta, inclusive as que não dá para vincular, com o aviso do
// motivo. Esconder a caixa problemática só faria a pessoa procurar por ela.
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import assistenteIaApi from 'dashboard/api/assistenteIa';
import { useAssistenteIaApi } from '../composables/useAssistenteIaApi';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TabelaVazia from '../components/TabelaVazia.vue';

const { t } = useI18n();
const { carregando, erro, chamar } = useAssistenteIaApi();

const caixas = ref([]);
const assistentes = ref([]);
const vinculando = ref(null);
const escolhido = ref(null);
const aceitaAtribuicao = ref(false);
const salvando = ref(false);
const paraDesvincular = ref(null);

// A lista de caixas é a chamada principal; a de assistentes é apoio. A segunda não pode apagar
// o erro da primeira — era o mesmo defeito da tela de editar assistente (AIA-08, C1).
const carregar = async () => {
  const dados = await chamar(assistenteIaApi.caixas());
  if (dados) caixas.value = dados.payload;

  const lista = await chamar(assistenteIaApi.assistentes(), { limparErro: false });
  if (lista) assistentes.value = lista.payload;
};

const abrirVinculo = caixa => {
  vinculando.value = caixa;
  escolhido.value = assistentes.value[0]?.id || null;
  aceitaAtribuicao.value = false;
};

const vincular = async () => {
  if (!escolhido.value) return;

  salvando.value = true;
  const dados = await chamar(
    assistenteIaApi.vincularCaixa(escolhido.value, {
      inbox_id: vinculando.value.inbox_id,
      aceita_atribuicao_automatica: aceitaAtribuicao.value,
    })
  );
  salvando.value = false;
  if (!dados) return;

  vinculando.value = null;
  carregar();
};

const desvincular = async () => {
  const alvo = paraDesvincular.value;
  paraDesvincular.value = null;
  if (!alvo) return;

  const feito = await chamar(assistenteIaApi.desvincularCaixa(alvo.vinculo.id));
  if (feito !== null) carregar();
};

const alternarAtiva = async caixa => {
  const feito = await chamar(
    assistenteIaApi.editarCaixa(caixa.vinculo.id, { ativa: !caixa.vinculo.ativa })
  );
  // A ação falhava, o erro aparecia, e o `carregar()` seguinte o apagava na hora: a pessoa via
  // um piscar e nada mudava na tela. É o C1 da AIA-07, que continuava aqui (AIA-09, C3).
  if (feito === null) return;

  carregar();
};

onMounted(carregar);
</script>

<template>
  <section class="flex flex-col gap-4">
    <Banner v-if="erro" color="ruby">{{ erro }}</Banner>

    <div>
      <h2 class="text-heading-2 text-n-slate-12">
        {{ t('ASSISTENTE_IA.CAIXAS.TITULO') }}
      </h2>
      <p class="mb-0 text-n-slate-11">{{ t('ASSISTENTE_IA.CAIXAS.DESCRICAO') }}</p>
    </div>

    <div
      v-if="vinculando"
      class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <p class="mb-0 font-medium text-n-slate-12">
        {{ t('ASSISTENTE_IA.CAIXAS.VINCULAR') }} — {{ vinculando.nome }}
      </p>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ASSISTENTE_IA.CAIXAS.ESCOLHA_ASSISTENTE') }}
        <select
          v-model="escolhido"
          class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
        >
          <option
            v-for="assistente in assistentes"
            :key="assistente.id"
            :value="assistente.id"
          >
            {{ assistente.nome }}
          </option>
        </select>
      </label>

      <label
        v-if="vinculando.atribuicao_automatica"
        class="flex items-start gap-2 text-sm text-n-slate-11"
      >
        <input v-model="aceitaAtribuicao" type="checkbox" class="mt-1" />
        <span>{{ t('ASSISTENTE_IA.CAIXAS.CONFIRMA_ATRIBUICAO') }}</span>
      </label>

      <div class="flex gap-2">
        <Button
          :label="t('ASSISTENTE_IA.CAIXAS.VINCULAR')"
          :is-loading="salvando"
          size="sm"
          @click="vincular"
        />
        <Button
          :label="t('ASSISTENTE_IA.COMUM.CANCELAR')"
          variant="faded"
          color="slate"
          size="sm"
          @click="vinculando = null"
        />
      </div>
    </div>

    <div v-if="carregando && !caixas.length" class="flex justify-center py-16">
      <Spinner />
    </div>

    <TabelaVazia
      v-else-if="!caixas.length"
      :mensagem="t('ASSISTENTE_IA.CAIXAS.VAZIO')"
    />

    <table v-else class="w-full text-sm text-left">
      <thead class="border-b text-n-slate-11 border-n-weak">
        <tr>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CAIXAS.NOME') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CAIXAS.CANAL') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CAIXAS.VINCULADA') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CAIXAS.ATIVA') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.CAIXAS.AVISOS') }}</th>
          <th class="py-2 font-medium text-right">
            {{ t('ASSISTENTE_IA.ASSISTENTES.ACOES') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="caixa in caixas" :key="caixa.inbox_id" class="border-b border-n-weak">
          <td class="py-3 text-n-slate-12">{{ caixa.nome }}</td>
          <td class="py-3 text-n-slate-11">
            {{ caixa.tipo_canal }}
            <span v-if="caixa.provedor_canal">· {{ caixa.provedor_canal }}</span>
          </td>
          <td class="py-3 text-n-slate-11">
            {{ caixa.vinculo?.assistente?.nome || t('ASSISTENTE_IA.CAIXAS.SEM_VINCULO') }}
          </td>
          <td class="py-3">
            <button
              v-if="caixa.vinculo"
              class="px-2 py-0.5 text-xs rounded-full"
              :class="
                caixa.vinculo.ativa
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-slate-3 text-n-slate-11'
              "
              @click="alternarAtiva(caixa)"
            >
              {{
                caixa.vinculo.ativa
                  ? t('ASSISTENTE_IA.COMUM.ATIVO')
                  : t('ASSISTENTE_IA.COMUM.INATIVO')
              }}
            </button>
            <span v-else class="text-n-slate-11">
              {{ t('ASSISTENTE_IA.COMUM.NAO_INFORMADO') }}
            </span>
          </td>
          <td class="py-3">
            <div class="flex flex-col gap-1">
              <span
                v-if="caixa.bot_ativo"
                class="px-2 py-0.5 text-xs rounded-full bg-n-amber-3 text-n-amber-11 w-fit"
                :title="t('ASSISTENTE_IA.CAIXAS.BOT_TOOLTIP')"
              >
                {{ t('ASSISTENTE_IA.CAIXAS.BOT_LIGADO') }}
              </span>
              <span
                v-if="caixa.atribuicao_automatica"
                class="px-2 py-0.5 text-xs rounded-full bg-n-amber-3 text-n-amber-11 w-fit"
              >
                {{ t('ASSISTENTE_IA.CAIXAS.ATRIBUICAO') }}
              </span>
            </div>
          </td>
          <td class="py-3">
            <div class="flex justify-end gap-2">
              <Button
                v-if="!caixa.vinculo"
                :label="t('ASSISTENTE_IA.CAIXAS.VINCULAR')"
                variant="faded"
                color="slate"
                size="xs"
                :disabled="caixa.bot_ativo"
                :title="caixa.bot_ativo ? t('ASSISTENTE_IA.CAIXAS.BOT_TOOLTIP') : ''"
                @click="abrirVinculo(caixa)"
              />
              <button
                v-else
                class="text-n-ruby-11 hover:underline"
                @click="paraDesvincular = caixa"
              >
                {{ t('ASSISTENTE_IA.CAIXAS.DESVINCULAR') }}
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div
      v-if="paraDesvincular"
      class="flex flex-col gap-2 p-4 border rounded-xl border-n-ruby-4 bg-n-ruby-2"
    >
      <p class="mb-0 font-medium text-n-slate-12">
        {{ t('ASSISTENTE_IA.CAIXAS.DESVINCULAR_TITULO') }}
      </p>
      <p class="mb-0 text-n-slate-11">
        {{ t('ASSISTENTE_IA.CAIXAS.DESVINCULAR_TEXTO') }}
      </p>
      <div class="flex gap-2">
        <Button
          :label="t('ASSISTENTE_IA.CAIXAS.DESVINCULAR')"
          color="ruby"
          size="sm"
          @click="desvincular"
        />
        <Button
          :label="t('ASSISTENTE_IA.COMUM.CANCELAR')"
          variant="faded"
          color="slate"
          size="sm"
          @click="paraDesvincular = null"
        />
      </div>
    </div>
  </section>
</template>
